import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { BreakdownSlotKey } from '@jobos/shared';
import {
  useBreakdownDocuments,
  useCreateBreakdownDocument,
} from '@/hooks/useBreakdownDocuments';
import { BreakdownDocumentList } from './BreakdownDocumentList';

// Wraps a breakdown editor mount and resolves which document the editor
// should show based on the URL's ?docId param + the job's current document
// inventory. Behind VITE_BREAKDOWN_DOCS_ENABLED — when off, the wrapper
// is a passthrough (renders children with documentId=undefined, so the
// legacy snapshot-versions flow remains unchanged).
//
// Resolver rules (flag on):
//   - ?docId set        → render editor with that id.
//   - 0 documents       → POST a blank "Working" doc and navigate to it.
//   - 1 document        → render editor with that doc's id (one-doc job
//                         skips the list view).
//   - 2+ documents      → render BreakdownDocumentList; clicking a row
//                         sets ?docId to open the editor.

const FLAG_ENABLED = import.meta.env.VITE_BREAKDOWN_DOCS_ENABLED === 'true';

export interface BreakdownDocsWrapperProps {
  jobId: string;
  // Slot the editor is mounted for (carpet vs generic). Scopes the document
  // list query so switching project type doesn't show the other slot's docs.
  slotKey: BreakdownSlotKey;
  // Render-prop: the wrapper hands the resolved documentId (or undefined
  // when the flag is off) to the inner editor. The editor reads it via prop,
  // not via a context, so the data flow stays explicit.
  children: (props: { documentId: string | undefined }) => React.ReactNode;
}

export function BreakdownDocsWrapper({ jobId, slotKey, children }: BreakdownDocsWrapperProps) {
  if (!FLAG_ENABLED) {
    return <>{children({ documentId: undefined })}</>;
  }
  return (
    <BreakdownDocsWrapperFlagOn jobId={jobId} slotKey={slotKey}>
      {children}
    </BreakdownDocsWrapperFlagOn>
  );
}

function BreakdownDocsWrapperFlagOn({ jobId, slotKey, children }: BreakdownDocsWrapperProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const docIdParam = searchParams.get('docId');
  const { data: documents, isLoading } = useBreakdownDocuments(jobId, slotKey);
  const createDoc = useCreateBreakdownDocument(jobId);

  // Auto-create a blank Working doc the first time a job lands in the
  // editor with zero documents. Guarded by a ref so a slow API call
  // doesn't trigger duplicate POSTs across re-renders.
  const createdRef = useRef(false);
  useEffect(() => {
    if (
      !isLoading &&
      documents &&
      documents.length === 0 &&
      !createdRef.current &&
      !createDoc.isPending
    ) {
      createdRef.current = true;
      // "v1" rather than "Working" — the latter is a backfill artifact that
      // described docs synthesized from the legacy working tables. On a
      // brand-new job that meaning is gone; "v1" reads as an intentional
      // starting point the user can rename or branch from.
      createDoc.mutate(
        { name: 'v1', tag: null },
        {
          onSuccess: (doc) => {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set('docId', doc.id);
                return next;
              },
              { replace: true },
            );
          },
          onError: () => {
            // Allow a retry on next render if the create failed
            // (network blip, validation race, etc).
            createdRef.current = false;
          },
        },
      );
    }
  }, [isLoading, documents, createDoc, setSearchParams]);

  if (isLoading || !documents) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  // Explicit ?docId always wins. Note: we don't validate the id against the
  // documents list — the editor's own fetch will surface a 404 / empty
  // state if the id is stale. This keeps the wrapper simple and avoids a
  // confusing "the doc you linked to no longer exists" redirect.
  if (docIdParam) {
    return <>{children({ documentId: docIdParam })}</>;
  }

  if (documents.length === 0) {
    // We're mid-create. Show a spinner — the effect above will set ?docId
    // once the POST resolves.
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (documents.length === 1) {
    return <>{children({ documentId: documents[0]!.id })}</>;
  }

  // 2+ documents and no explicit ?docId: present the list view.
  const navigateToDoc = (docId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('docId', docId);
      return next;
    });
  };
  return <BreakdownDocumentList jobId={jobId} slotKey={slotKey} onOpen={navigateToDoc} />;
}
